import React, { useState, useEffect, useMemo } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import { Participant } from "@shared/schema";

interface ParticipantSearchInputProps {
  participants: Participant[];
  existingParticipantIds: Set<number>;
  value: string;
  onSearchChange: (value: string) => void;
  onParticipantSelect: (participant: Participant) => void;
  onClear: () => void;
  placeholder?: string;
  className?: string;
}

export function ParticipantSearchInput({
  participants,
  existingParticipantIds,
  value,
  onSearchChange,
  onParticipantSelect,
  onClear,
  placeholder = "Search for participant or enter new name...",
  className,
}: ParticipantSearchInputProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const { isChrome, isMobileSafari } = useBrowserDetection();

  // Filter participants based on search term
  const filteredParticipants = useMemo(() => {
    if (!value.trim()) return [];
    
    const searchLower = value.toLowerCase();
    return participants.filter((p) => {
      const nameMatch = p.name.toLowerCase().includes(searchLower);
      const emailMatch = p.email?.toLowerCase().includes(searchLower);
      return nameMatch || emailMatch;
    });
  }, [value, participants]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".participant-search-container")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSelect = (participant: Participant) => {
    if (!existingParticipantIds.has(participant.id)) {
      onParticipantSelect(participant);
      onSearchChange(participant.name);
      setShowDropdown(false);
    }
  };

  const handleClear = () => {
    onClear();
    setShowDropdown(false);
  };

  return (
    <div className={cn("relative participant-search-container", className)}>
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={value}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onClick={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-10 pr-10"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {showDropdown && filteredParticipants.length > 0 && (
        <div 
          className={`absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-auto border ${
            isChrome || isMobileSafari
              ? 'bg-white border-gray-200' 
              : 'bg-white/20 backdrop-blur-xl border-white/30'
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{ 
            backgroundColor: (isChrome || isMobileSafari) ? 'white' : undefined,
            backdropFilter: (isChrome || isMobileSafari) ? 'none' : undefined
          }}
        >
          {filteredParticipants.map((participant) => {
            const isAlreadyAdded = existingParticipantIds.has(participant.id);
            return (
              <div
                key={participant.id}
                onClick={() => handleSelect(participant)}
                className={cn(
                  "px-4 py-3 border-b border-white/30 last:border-b-0",
                  isAlreadyAdded 
                    ? "opacity-50 cursor-not-allowed bg-white/20" 
                    : "hover:bg-white/40 cursor-pointer"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {participant.name}
                      {isAlreadyAdded && (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                          Already added
                        </Badge>
                      )}
                    </div>
                    {participant.email && (
                      <div className="text-sm text-slate-500">
                        {participant.email}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {participant.lookingForBand && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        Looking for Band
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}